import React, { useEffect } from 'react';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@web/components/ui/table';
import UpdateContactDialog from './UpdateContactDialog';
import { ContactDataForTable } from '@shared/types/contact-table-contact.type';
import { ActionsMenu } from '@web/components/ui/actions-menu';
import { Pagination } from '@web/components/ui/pagination';
import { useNavigate } from '@tanstack/react-router';

type ContactTableProps = {
  contacts: ContactDataForTable[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
  isLoading?: boolean;
};

export default function ContactTable({
  contacts,
  pagination,
  isLoading,
}: ContactTableProps) {
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] =
    React.useState<boolean>(false);
  const [updateDialogContactId, setUpdateDialogContactId] = React.useState<
    string | null
  >(null);

  const navigate = useNavigate();

  // Redirect to details page when clicking the row
  const onRowClick = (contactId: string): void => {
    void navigate({
      to: `/dashboard/admin/contact/details/${contactId}`,
      search: (current: URLSearchParams) => current,
    });
  };
  
  const onEditClick = (contactId: string): void => {
    setIsUpdateDialogOpen(true);
    setUpdateDialogContactId(contactId);
  };

  useEffect(() => {
    if (!isUpdateDialogOpen) {
      setUpdateDialogContactId(null);
    }
  }, [isUpdateDialogOpen]);

  const placeholderValue = '-';

  // Helper to add responsive classes for skeleton cells (indexes 4-7 are hidden on mobile)
  const getSkeletonCellClass = (cellIndex: number): string =>
    [4, 5, 6, 7].includes(cellIndex) ? 'hidden md:table-cell' : '';

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>First Name</TableHead>
            <TableHead>Last Name</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead className="hidden md:table-cell">Email</TableHead>
            <TableHead className="hidden md:table-cell">Sales Rep</TableHead>
            <TableHead>Signed Contract(s)</TableHead>
            <TableHead className="hidden md:table-cell">Payment(s)</TableHead>
            <TableHead className="hidden md:table-cell">
              Last Communication
            </TableHead>
            <TableHead>{/* Actions column */}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, rowIndex) => {
                return (
                  <TableRow
                    key={`skeleton-${rowIndex.toString()}`}
                    className="animate-pulse"
                  >
                    {Array.from({ length: 9 }).map((_, cellIndex) => {
                      return (
                        <TableCell
                          key={`skeleton-cell-${cellIndex.toString()}`}
                          className={getSkeletonCellClass(cellIndex)}
                        >
                          <div className="bg-muted h-4 w-[80%] rounded" />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            : contacts.map((contact) => (
                <TableRow
                  key={contact.id}
                  onClick={() => {
                    onRowClick(contact.id);
                  }}
                  className="hover:bg-muted/50 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onRowClick(contact.id);
                    }
                  }}
                  aria-label={`View details for ${contact.firstName} ${contact.lastName}`}
                >
                  <TableCell>{contact.firstName}</TableCell>
                  <TableCell>{contact.lastName}</TableCell>
                  <TableCell>{contact.phoneNumber}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {contact.email ?? placeholderValue}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {contact.salesRep}
                  </TableCell>
                  <TableCell>
                    {contact.hasSignedContracts ? 'YES' : 'NO'}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {contact.hasPayments ? 'YES' : 'NO'}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {contact.lastCommunicationDate
                      ? format(
                          new Date(contact.lastCommunicationDate),
                          'yyyy/MM/dd',
                        )
                      : placeholderValue}
                  </TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <ActionsMenu
                      actions={{
                        details: () => {
                          onRowClick(contact.id);
                        },
                        edit: () => {
                          onEditClick(contact.id);
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>

      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          pageSize={pagination.pageSize}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
        />
      )}

      <UpdateContactDialog
        contactId={updateDialogContactId}
        isOpen={isUpdateDialogOpen}
        setIsOpen={setIsUpdateDialogOpen}
      />
    </>
  );
}
